import React from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";

interface NewClientsOnboardedProps {
  agents: { id: number; name: string }[];
  data: { month: string; [key: number]: number }[];
}

const NewClientsOnboarded: React.FC<NewClientsOnboardedProps> = ({
  agents,
  data,
}) => {
  console.log("🔍 NewClientsOnboarded: Data received:", data);
  console.log("🔍 NewClientsOnboarded: Data length:", data?.length || 0);
  console.log("🔍 NewClientsOnboarded: Agents:", agents);
  
  // Need at least 2 data points for a line chart to show lines
  const hasData = data && data.length >= 2;

  return (
    <Card className="w-full h-full flex flex-col">
      <CardHeader className="pb-4 mb-4 border-b border-gray-200 flex-shrink-0">
        <CardTitle className="text-accent text-2xl">New Clients Onboarded</CardTitle>
        <CardDescription className="text-sm mb-2">New clients onboarded by each agent</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 flex-1 min-h-0 flex flex-col">
        {!hasData ? (
          <div className="flex items-center justify-center h-[400px] text-muted-foreground text-sm">
            <p>{data && data.length === 1 
              ? "Insufficient data: Need at least 2 months to display trends" 
              : "No client onboarding data available"}</p>
          </div>
        ) : (
          <div className="mt-auto w-full flex items-end justify-start -mb-4">
            <ResponsiveContainer width="100%" height={400}>
            <LineChart data={data} margin={{ top: 10, right: 15, left: 5, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 11, fill: '#6b7280' }}
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#6b7280' }}
                width={40}
                domain={[0, 'dataMax + 2']}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{ fontSize: '12px', padding: '8px' }}
                labelStyle={{ fontSize: '11px', marginBottom: '4px' }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                iconType="line"
              />

              {agents.map((agent, index) => (
                <Line
                  key={agent.id}
                  type="linear"
                  dataKey={agent.id.toString()}
                  name={agent.name}
                  stroke={`hsl(var(--chart-${index + 1}))`}
                  strokeWidth={2}
                  dot={{ r: 3, fill: `hsl(var(--chart-${index + 1}))` }}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex-shrink-0 border-t border-gray-100 pt-3 pb-3">
        <p className="text-sm text-muted-foreground leading-relaxed">
          <span className="font-medium text-foreground">Insight:</span> New client acquisition trends by agent showing monthly onboarding patterns.
        </p>
      </CardFooter>
    </Card>
  );
};

export default NewClientsOnboarded;
